import {
  DESC,
  Filters,
  FiltersLayout,
  List,
  ListPageLayout,
  LoadingParams,
  QuickFilter,
  SearchInput,
} from 'framework/list';
import useEmployeesDashboardService from '../useEmployeesDashboardService';
import { DELEGATED_EMPLOYEE_ZONES_ALIASES, READ_DELEGATED_DASHBOARD_ENTRIES } from '../constants';
import EmployeesDashboardZone from './EmployeesDashboardZone/EmployeesDashboardZone';
import { EmployeeZoneAlias } from '../types';
import EmployeeByRetentionRiskStatusFilter from './filter/EmployeeByRetentionRiskStatusFilter';
import { EmployeeFilter } from 'domain/shared/employee';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import { useAsyncValue } from 'framework/utils';
import { EmployeePositionFilter } from 'domain/shared/employee';
import { useAuthorization } from 'framework/authorization';
import employeeDataGetters from '../utils/employeeDataGetters';
import { EMPLOYEE_ZONES_ALIASES } from 'domain/shared/employee';
import { DashboardProvider } from 'framework/ui/dashboard';
import { TechnologiesFilter } from 'domain/shared/career';

const EmployeesDashboard = () => {
  const { getAllEmployessForDashboardParallelByZones, getCombinedEmployeesForDashboard } =
    useEmployeesDashboardService();
  const { getUserAlias } = useAuthenticatedUserService();
  const userAlias = useAsyncValue(() => getUserAlias());

  const { isGranted } = useAuthorization();

  const getEmployeesForDashboardAccordingToPermissions = async (loadParams: LoadingParams) => {
    if (isGranted(READ_DELEGATED_DASHBOARD_ENTRIES)) {
      return getCombinedEmployeesForDashboard(loadParams);
    }

    return getAllEmployessForDashboardParallelByZones(loadParams);
  };

  const filters = (
    <Filters>
      <FiltersLayout
        quickFilters={[
          <QuickFilter
            label="Assigned to me"
            state={{ 'specification:employeeByRetentionRiskAssigneeAliasSpecification': { eq: userAlias } }}
          />,
        ]}
      >
        <EmployeeFilter source="specification:employeeByRetentionRiskAssigneeAliasSpecification" label="Assignee" />
        <EmployeeByRetentionRiskStatusFilter
          source="specification:employeeByRetentionRiskStatusSpecification"
          label="Risk status"
        />
        <EmployeePositionFilter source="positionId" label="Position" />
        <TechnologiesFilter source="specializationInfo.technologyId" label="Technology" />
      </FiltersLayout>
    </Filters>
  );

  return (
    <List
      key="employees-dashboard"
      getDataMethod={getEmployeesForDashboardAccordingToPermissions}
      defaultSortOrder={DESC}
      defaultSortField="createdAt"
      pageSize={200}
    >
      <ListPageLayout
        filter={filters}
        sxPaper={{ p: '10px !important' }}
        search={<SearchInput label="worker name or ID" />}
      >
        <DashboardProvider dataGetters={employeeDataGetters}>
          {Object.values(
            isGranted(READ_DELEGATED_DASHBOARD_ENTRIES) ? DELEGATED_EMPLOYEE_ZONES_ALIASES : EMPLOYEE_ZONES_ALIASES,
          )
            .toReversed()
            .map(zone => {
              return <EmployeesDashboardZone key={zone} zone={zone as EmployeeZoneAlias} />;
            })}
        </DashboardProvider>
      </ListPageLayout>
    </List>
  );
};

export default EmployeesDashboard;
